/**
 * useSwipeToClose - Horizontal swipe gesture for dismissing a slide-in overlay.
 *
 * Attach the returned `ref` to the panel that should follow the user's finger.
 * While the user drags in the dismiss direction the `dragX` value tracks the
 * horizontal displacement so the panel can translate with the gesture; on
 * release, if the displacement crossed `threshold` the hook calls `onClose`,
 * otherwise it resets to 0 and the panel snaps back via its normal CSS
 * transition.
 *
 * - `direction="right"` (panel enters from the right): only positive dx is
 *   treated as dismiss (swipe left-to-right).
 * - `direction="left"` (panel enters from the left, e.g. MobileDrawer): only
 *   negative dx is treated as dismiss (swipe right-to-left).
 *
 * Vertical-dominant gestures are abandoned so scroll containers inside the
 * panel keep working normally.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

function useSwipeToClose({
  open,
  direction = 'right',
  threshold = 80,
  onClose,
  enabled = true,
} = {}) {
  // Callback ref so the listener-attach effect re-runs when the panel mounts
  // (or unmounts) — overlays like `SlideOverlay` gate mount on a transition
  // state, so the panel is not in the DOM on the first render where
  // `open === true`. A `useRef` would silently miss that attachment.
  const [el, setEl] = useState(null);
  const ref = useCallback((node) => {
    setEl(node);
  }, []);
  const [dragX, setDragX] = useState(0);

  const startRef = useRef(null);
  const dragRef = useRef(0);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!el || !open || !enabled) return undefined;

    const reset = () => {
      dragRef.current = 0;
      setDragX(0);
    };

    const handleStart = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      startRef.current = { x: t.clientX, y: t.clientY, cancelled: false };
      dragRef.current = 0;
    };

    const handleMove = (e) => {
      const s = startRef.current;
      if (!s || s.cancelled) return;
      const t = e.touches?.[0];
      if (!t) return;
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      // Vertical-dominant motion — hand off to scroll.
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) {
        s.cancelled = true;
        reset();
        return;
      }
      const effective = direction === 'right' ? Math.max(0, dx) : Math.min(0, dx);
      dragRef.current = effective;
      setDragX(effective);
    };

    const handleEnd = () => {
      const s = startRef.current;
      startRef.current = null;
      if (!s || s.cancelled) {
        reset();
        return;
      }
      const shouldClose =
        direction === 'right'
          ? dragRef.current >= threshold
          : dragRef.current <= -threshold;
      reset();
      if (shouldClose) onCloseRef.current?.();
    };

    el.addEventListener('touchstart', handleStart, { passive: true });
    el.addEventListener('touchmove', handleMove, { passive: true });
    el.addEventListener('touchend', handleEnd);
    el.addEventListener('touchcancel', handleEnd);
    return () => {
      el.removeEventListener('touchstart', handleStart);
      el.removeEventListener('touchmove', handleMove);
      el.removeEventListener('touchend', handleEnd);
      el.removeEventListener('touchcancel', handleEnd);
    };
  }, [el, open, direction, threshold, enabled]);

  return { ref, dragX };
}

export default useSwipeToClose;
